import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Locale, TranslationKey, translations } from './translations';

const LOCALE_KEY = 'viu_movies_locale';
const DEFAULT_LOCALE: Locale = 'en';
const supportedLocales = Object.keys(translations) as Locale[];

type TranslationParams = Record<string, string | number>;

type I18nContextValue = {
  locale: Locale;
  locales: Locale[];
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, params?: TranslationParams) => string;
  formatDateTime: (value: string | number | Date) => string;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const isLocale = (value: string | null): value is Locale => Boolean(value && supportedLocales.includes(value as Locale));

export const getCurrentLocale = (): Locale => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  const storedLocale = window.localStorage.getItem(LOCALE_KEY);
  if (isLocale(storedLocale)) return storedLocale;

  const browserLocale = window.navigator.language.toLowerCase();
  if (browserLocale === 'zh-hans' || browserLocale.startsWith('zh-cn') || browserLocale.startsWith('zh-sg')) return 'zh-Hans';
  if (browserLocale.startsWith('zh')) return 'zh-Hant';

  return DEFAULT_LOCALE;
};

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => getCurrentLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = (nextLocale: Locale) => {
    window.localStorage.setItem(LOCALE_KEY, nextLocale);
    setLocaleState(nextLocale);
  };

  const value = useMemo<I18nContextValue>(() => {
    const t = (key: TranslationKey, params: TranslationParams = {}) => {
      const template = String(translations[locale][key] || translations.en[key]);
      return Object.entries(params).reduce((result, [paramKey, paramValue]) => result.split(`{{${paramKey}}}`).join(String(paramValue)), template);
    };

    const formatDateTime = (dateValue: string | number | Date) => new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(dateValue));

    return {
      locale,
      locales: supportedLocales,
      setLocale,
      t,
      formatDateTime,
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used inside I18nProvider');
  }
  return context;
};
